import {isFeatureEnabled} from 'feature-flags';
import {Icon, Show} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {
  useGetCarVerticalConfigQuery,
  useGetCebiaAutotracerAndReportConfigQuery,
  useGetCebiaFeatureDecoderConfigQuery,
  useGetCebiaRokvyConfigQuery,
  useGetCebiaVinDecoderConfigQuery,
  useGetOmneticVinDecoderConfigQuery,
  useGetTenantQuery,
  usePatchCarVerticalConfigMutation,
  usePatchCebiaAutotracerAndReportConfigMutation,
  usePatchCebiaFeatureDecoderConfigMutation,
  usePatchCebiaRokvyConfigMutation,
  usePatchCebiaVinDecoderConfigMutation,
  usePatchOmneticVinDecoderConfigMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {StackItem, Wrapper} from '@dms/settings/shared';
import {handleApiError, useIsLicenceEnabled} from '@dms/shared';

import {buildArray} from 'shared/web';

import {VehicleServiceContentItem} from './VehicleServiceContentItem';

export function VehicleServicesList() {
  const {data: cebiaAutotracerAndReport} = useGetCebiaAutotracerAndReportConfigQuery();
  const {data: cebiaFeatureDecoder} = useGetCebiaFeatureDecoderConfigQuery();
  const {data: cebiaRokvy} = useGetCebiaRokvyConfigQuery();
  const {data: omneticVinDecoder} = useGetOmneticVinDecoderConfigQuery();
  const {data: cebiaVinDecoder} = useGetCebiaVinDecoderConfigQuery();
  const {data: carVertical} = useGetCarVerticalConfigQuery();
  const {data: tenantData} = useGetTenantQuery();
  const [patchCebiaAutotracerAndReportConfig] = usePatchCebiaAutotracerAndReportConfigMutation();
  const [patchCebiaFeatureDecoderConfig] = usePatchCebiaFeatureDecoderConfigMutation();
  const [patchCebiaRokvyConfig] = usePatchCebiaRokvyConfigMutation();
  const [patchOmneticVinDecoder] = usePatchOmneticVinDecoderConfigMutation();
  const [patchCebiaVinDecoder] = usePatchCebiaVinDecoderConfigMutation();
  const [patchCarVerticalConfig] = usePatchCarVerticalConfigMutation();

  const isBelgianTenant = tenantData?.country === 'BEL';
  const {isCarPassEnabled} = useIsLicenceEnabled();

  const updateAutotracerAndReport = (enabled: boolean) => {
    patchCebiaAutotracerAndReportConfig({
      cebiaAutotracerAndReportConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaVinDecoder = (enabled: boolean) => {
    if (enabled) {
      patchOmneticVinDecoder({body: {enabled: false}});
    }

    patchCebiaVinDecoder({
      body: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaFeatureDecoder = (enabled: boolean) => {
    patchCebiaFeatureDecoderConfig({
      cebiaFeatureDecoderConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaRokvy = (enabled: boolean) => {
    patchCebiaRokvyConfig({
      cebiaRokvyConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateOmneticVinDecoder = (enabled: boolean) => {
    if (!isFeatureEnabled(featureFlags.OMNETIC_VIN_DECODER)) {
      throw new Error('Feature flag OMNETIC_VIN_DECODER is not enabled!');
    }

    if (enabled) {
      patchCebiaVinDecoder({body: {enabled: false}});
    }

    patchOmneticVinDecoder({
      body: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCarVertical = (enabled: boolean) => {
    patchCarVerticalConfig({
      carVerticalConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const items = buildArray<{title: ReactNode; url?: string}>([
    {
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-equipment-autotrace"
          isDisabled={!cebiaAutotracerAndReport?.credentials}
          switcherOn={cebiaAutotracerAndReport?.enabled}
          onSwitcherToggle={updateAutotracerAndReport}
          title={i18n.t('page.integrations.labels.cebia')}
          label={i18n.t('page.integrations.labels.cebiaAutotraceDescription')}
        />
      ),
      url: settingsRoutes.vehicleServicesCebia,
    },
    {
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-vin-decoder"
          switcherOn={cebiaVinDecoder?.enabled}
          onSwitcherToggle={updateCebiaVinDecoder}
          title={i18n.t('page.integrations.labels.cebiaVinDecoder')}
          label={i18n.t('page.integrations.labels.cebiaVinDecoderSubtext')}
        />
      ),
    },
    {
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-equipment-switcher"
          isDisabled={!cebiaFeatureDecoder?.credentials}
          switcherOn={cebiaFeatureDecoder?.enabled}
          onSwitcherToggle={updateCebiaFeatureDecoder}
          title={i18n.t('page.integrations.labels.cebiaEquipment')}
          label={i18n.t('page.integrations.labels.cebiaEquipmentSubtext')}
        />
      ),
      url: settingsRoutes.vehicleServicesFeatures,
    },
    {
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-rokvy-switcher"
          isDisabled={!cebiaRokvy?.credentials}
          switcherOn={cebiaRokvy?.enabled}
          onSwitcherToggle={updateCebiaRokvy}
          title={i18n.t('page.integrations.labels.cebiaRokvy')}
          label={i18n.t('page.integrations.labels.cebiaRokvyDescription')}
        />
      ),
      url: settingsRoutes.vehicleServicesRokVy,
    },
  ])
    .whenFeatureEnabled(featureFlags.CAR_VERTICAL_INTEGRATION, {
      title: (
        <VehicleServiceContentItem
          switcherName="car-vertical-switcher"
          switcherOn={carVertical?.enabled}
          onSwitcherToggle={updateCarVertical}
          title={i18n.t('page.integrations.labels.carVertical')}
          label={i18n.t('page.integrations.labels.carVerticalDescription')}
        />
      ),
    })
    .whenFeatureEnabled(featureFlags.OMNETIC_VIN_DECODER, {
      title: (
        <VehicleServiceContentItem
          switcherName="omnetic-vin-decoder"
          switcherOn={omneticVinDecoder?.enabled}
          onSwitcherToggle={updateOmneticVinDecoder}
          title={i18n.t('page.integrations.labels.omneticVinDecoder')}
          label={i18n.t('page.integrations.labels.omneticVinDecoderDescription')}
        />
      ),
    });

  return (
    <Wrapper>
      <Show when={isCarPassEnabled && isBelgianTenant}>
        <VehicleServicesStackItemInner
          key="carpass"
          to={settingsRoutes.vehicleServicesCarpass}
          data-testid={testIds.settings.vehicleServicesCarpass()}
        >
          {i18n.t('page.integrations.labels.carpassConfig')}
          <Icon value="navigation/chevron_right" />
        </VehicleServicesStackItemInner>
      </Show>
      {items.map(({title, url}, index) => (
        <VehicleServicesStackItemInner
          key={url}
          to={url}
          data-testid={testIds.settings.vehicleServices(`link-${index}`)}
        >
          {title}
          <Show when={isNotNil(url)}>
            <Icon value="navigation/chevron_right" />
          </Show>
        </VehicleServicesStackItemInner>
      ))}
    </Wrapper>
  );
}

const VehicleServicesStackItemInner = styled(StackItem)`
  align-items: start;
`;
